/**
 * Audit Annotation System Hooks
 * React Query hooks for audit findings, sessions, and responses
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import apiClient from "../lib/apiClient";
import { FORM_MUTATION_META } from "../utils/formErrorHandler";
import {
  AuditSession,
  AuditFinding,
  AuditFindingResponse,
  CreateAuditSessionInput,
  UpdateAuditSessionInput,
  CreateAuditFindingInput,
  UpdateAuditFindingInput,
  CreateFindingResponseInput,
  ResidentDetailData,
  IncidentDetailData,
  CarePlanDetailData,
} from "../types/audit";

// ============================================================================
// Audit Sessions
// ============================================================================

/**
 * Fetch all audit sessions for current auditor
 */
export function useAuditSessions(facilityId?: string) {
  return useQuery({
    queryKey: ["audit-sessions", facilityId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (facilityId) params.append("facility_id", facilityId);

      const response = await apiClient.get<{ data: AuditSession[] }>(
        `/audit/sessions?${params.toString()}`,
      );
      return response.data.data;
    },
  });
}

/**
 * Fetch a single audit session by ID
 */
export function useAuditSession(
  sessionId: string | undefined,
  options?: UseQueryOptions<AuditSession>,
) {
  return useQuery({
    queryKey: ["audit-session", sessionId],
    queryFn: async () => {
      if (!sessionId) throw new Error("Session ID required");
      const response = await apiClient.get<{ data: AuditSession }>(
        `/audit/sessions/${sessionId}`,
      );
      return response.data.data;
    },
    enabled: !!sessionId,
    ...options,
  });
}

/**
 * Create a new audit session
 */
export function useCreateAuditSession() {
  const queryClient = useQueryClient();

  return useMutation({
    // US-082: SessionCreationModal owns error UX (onSubmitSafe → handleFormErrors).
    meta: FORM_MUTATION_META,
    mutationFn: async (input: CreateAuditSessionInput) => {
      const response = await apiClient.post<{ data: AuditSession }>(
        "/audit/sessions",
        input,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["audit-sessions"] });
    },
  });
}

/**
 * Update an existing audit session
 */
export function useUpdateAuditSession(sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // US-082: ReportSummarySection owns error UX (explicit handleFormErrors catch).
    meta: FORM_MUTATION_META,
    mutationFn: async (input: UpdateAuditSessionInput) => {
      const response = await apiClient.put<{ data: AuditSession }>(
        `/audit/sessions/${sessionId}`,
        input,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["audit-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["audit-session", sessionId] });
    },
  });
}

/**
 * US-112 Phase 1b: withdraw (retract) an audit session. Compliance records are
 * never hard-deleted — the DELETE route now stamps a withdrawal with a required
 * reason and keeps the row + all findings intact.
 */
export function useWithdrawAuditSession() {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { errorFallback: "Failed to withdraw session. Please try again." },
    mutationFn: async (input: { id: string; withdrawal_reason: string }) => {
      const response = await apiClient.delete<{ data: { withdrawn: boolean } }>(
        `/audit/sessions/${input.id}`,
        { data: { withdrawal_reason: input.withdrawal_reason } },
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["audit-sessions"] });
    },
  });
}

/**
 * Complete an audit session
 */
export function useCompleteAuditSession(sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // US-082: SessionCompletionModal owns error UX (onSubmitSafe → handleFormErrors).
    meta: FORM_MUTATION_META,
    mutationFn: async (input: {
      completion_notes?: string;
      force?: boolean;
    }) => {
      const response = await apiClient.post<{
        data: { session: AuditSession; message: string };
      }>(`/audit/sessions/${sessionId}/complete`, input);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["audit-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["audit-session", sessionId] });
    },
  });
}

/**
 * Generate PDF report for an audit session
 */
export function useGenerateAuditReport(sessionId: string) {
  return useMutation({
    // US-081: error fallback centralized here; ReportSummarySection catch is sole caller.
    meta: { errorFallback: "Failed to generate PDF report" },
    mutationFn: async () => {
      const response = await apiClient.post(
        `/audit/sessions/${sessionId}/generate-report`,
        {},
        {
          responseType: "blob", // Important for PDF download
        },
      );
      return response.data;
    },
    onSuccess: (blob) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit-report-${sessionId}-${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}

// ============================================================================
// Audit Findings
// ============================================================================

/**
 * Fetch all findings for a session
 */
export function useAuditFindings(sessionId: string | undefined) {
  return useQuery({
    queryKey: ["audit-findings", sessionId],
    queryFn: async () => {
      if (!sessionId) throw new Error("Session ID required");
      const response = await apiClient.get<{ data: AuditFinding[] }>(
        `/audit/findings?session_id=${sessionId}`,
      );
      return response.data.data;
    },
    enabled: !!sessionId,
  });
}

/**
 * Fetch findings for a specific resource
 */
export function useResourceFindings(
  resourceType: string | undefined,
  resourceId: string | undefined,
) {
  return useQuery({
    queryKey: ["resource-findings", resourceType, resourceId],
    queryFn: async () => {
      if (!resourceType || !resourceId)
        throw new Error("Resource type and ID required");
      const response = await apiClient.get<{ data: AuditFinding[] }>(
        `/audit/findings?resource_type=${resourceType}&resource_id=${resourceId}`,
      );
      return response.data.data;
    },
    enabled: !!resourceType && !!resourceId,
  });
}

/**
 * Fetch a single audit finding by ID
 */
export function useAuditFinding(findingId: string | undefined) {
  return useQuery({
    queryKey: ["audit-finding", findingId],
    queryFn: async () => {
      if (!findingId) throw new Error("Finding ID required");
      const response = await apiClient.get<{ data: AuditFinding }>(
        `/audit/findings/${findingId}`,
      );
      return response.data.data;
    },
    enabled: !!findingId,
  });
}

/**
 * Create a new audit finding
 */
export function useCreateAuditFinding() {
  const queryClient = useQueryClient();

  return useMutation({
    // US-082: AddFindingModal owns error UX (onSubmitSafe → handleFormErrors).
    meta: FORM_MUTATION_META,
    mutationFn: async (input: CreateAuditFindingInput) => {
      const response = await apiClient.post<{ data: AuditFinding }>(
        "/audit/findings",
        input,
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      // Invalidate session findings
      queryClient.invalidateQueries({
        queryKey: ["audit-findings", data.session_id],
      });
      // Invalidate resource findings
      queryClient.invalidateQueries({
        queryKey: ["resource-findings", data.resource_type, data.resource_id],
      });
      // Invalidate session (to update finding count)
      queryClient.invalidateQueries({
        queryKey: ["audit-session", data.session_id],
      });
    },
  });
}

/**
 * Update an existing audit finding
 */
export function useUpdateAuditFinding(findingId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // US-082: EditFindingModal owns error UX (onSubmitSafe → handleFormErrors).
    meta: FORM_MUTATION_META,
    mutationFn: async (input: UpdateAuditFindingInput) => {
      const response = await apiClient.put<{ data: AuditFinding }>(
        `/audit/findings/${findingId}`,
        input,
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["audit-finding", findingId] });
      queryClient.invalidateQueries({
        queryKey: ["audit-findings", data.session_id],
      });
      queryClient.invalidateQueries({
        queryKey: ["resource-findings", data.resource_type, data.resource_id],
      });
    },
  });
}

/**
 * US-112 Phase 1b: withdraw (retract) an audit finding. Compliance findings are
 * never hard-deleted — the DELETE route stamps a withdrawal with a required
 * reason and keeps the row (visible, badged, excluded from open counts). Blocked
 * server-side once the finding has responses or its session is finalized.
 */
export function useWithdrawAuditFinding() {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { errorFallback: "Failed to withdraw finding" },
    mutationFn: async (input: { id: string; withdrawal_reason: string }) => {
      const response = await apiClient.delete<{ data: { withdrawn: boolean } }>(
        `/audit/findings/${input.id}`,
        { data: { withdrawal_reason: input.withdrawal_reason } },
      );
      return response.data.data;
    },
    onSuccess: () => {
      // Invalidate all finding queries
      queryClient.invalidateQueries({ queryKey: ["audit-findings"] });
      queryClient.invalidateQueries({ queryKey: ["resource-findings"] });
    },
  });
}

/**
 * Bulk update finding statuses (facility managers only)
 */
export function useBulkUpdateFindings() {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; BulkActionsToolbar catch is sole caller.
    meta: { errorFallback: "Failed to update findings" },
    mutationFn: async (input: { finding_ids: string[]; status: string }) => {
      const response = await apiClient.post<{
        data: { updated_count: number; findings: AuditFinding[] };
      }>("/audit/findings/bulk", input);
      return response.data.data;
    },
    onSuccess: () => {
      // Invalidate all finding queries to refresh lists
      queryClient.invalidateQueries({ queryKey: ["audit-findings"] });
      queryClient.invalidateQueries({ queryKey: ["resource-findings"] });
      queryClient.invalidateQueries({ queryKey: ["audit-sessions"] });
    },
  });
}

// ============================================================================
// Finding Responses
// ============================================================================

/**
 * Fetch responses for a finding
 */
export function useFindingResponses(findingId: string | undefined) {
  return useQuery({
    queryKey: ["finding-responses", findingId],
    queryFn: async () => {
      if (!findingId) throw new Error("Finding ID required");
      const response = await apiClient.get<{ data: AuditFindingResponse[] }>(
        `/audit/findings/${findingId}/responses`,
      );
      return response.data.data;
    },
    enabled: !!findingId,
  });
}

/**
 * Create a response to a finding
 */
export function useCreateFindingResponse(findingId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // US-082: FindingResponseModal owns error UX (onSubmitSafe → handleFormErrors).
    meta: FORM_MUTATION_META,
    mutationFn: async (input: CreateFindingResponseInput) => {
      const response = await apiClient.post<{ data: AuditFindingResponse }>(
        `/audit/findings/${findingId}/responses`,
        input,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["finding-responses", findingId],
      });
      queryClient.invalidateQueries({ queryKey: ["audit-finding", findingId] });
    },
  });
}

// ============================================================================
// Enhanced Detail Endpoints
// ============================================================================

/**
 * Fetch comprehensive resident data for audit purposes
 */
export function useAuditResidentDetail(
  facilityId: string | undefined,
  residentId: string | undefined,
) {
  return useQuery({
    queryKey: ["audit-resident-detail", facilityId, residentId],
    queryFn: async () => {
      if (!facilityId || !residentId)
        throw new Error("Facility ID and Resident ID required");
      const response = await apiClient.get<{ data: ResidentDetailData }>(
        `/audit/facilities/${facilityId}/residents/${residentId}`,
      );
      return response.data.data;
    },
    enabled: !!facilityId && !!residentId,
  });
}

/**
 * Fetch comprehensive incident data for audit purposes
 */
export function useAuditIncidentDetail(
  facilityId: string | undefined,
  incidentId: string | undefined,
) {
  return useQuery({
    queryKey: ["audit-incident-detail", facilityId, incidentId],
    queryFn: async () => {
      if (!facilityId || !incidentId)
        throw new Error("Facility ID and Incident ID required");
      const response = await apiClient.get<{ data: IncidentDetailData }>(
        `/audit/facilities/${facilityId}/incidents/${incidentId}`,
      );
      return response.data.data;
    },
    enabled: !!facilityId && !!incidentId,
  });
}

/**
 * Fetch comprehensive care plan data for audit purposes
 */
export function useAuditCarePlanDetail(
  facilityId: string | undefined,
  planId: string | undefined,
) {
  return useQuery({
    queryKey: ["audit-care-plan-detail", facilityId, planId],
    queryFn: async () => {
      if (!facilityId || !planId)
        throw new Error("Facility ID and Care Plan ID required");
      const response = await apiClient.get<{ data: CarePlanDetailData }>(
        `/audit/facilities/${facilityId}/care-plans/${planId}`,
      );
      return response.data.data;
    },
    enabled: !!facilityId && !!planId,
  });
}

// ============================================================================
// Audit Section Progress
// ============================================================================

export interface SectionProgress {
  id: string;
  session_id: string;
  section_name: string;
  is_complete: boolean;
  notes: string | null;
  completed_at: string | null;
  completed_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Fetch section progress for an audit session
 */
export function useSectionProgress(sessionId: string | undefined) {
  return useQuery({
    queryKey: ["section-progress", sessionId],
    queryFn: async () => {
      if (!sessionId) throw new Error("Session ID required");
      const response = await apiClient.get<{ data: SectionProgress[] }>(
        `/audit/sessions/${sessionId}/section-progress`,
      );
      return response.data.data;
    },
    enabled: !!sessionId,
  });
}

/**
 * Update section progress (mark complete/incomplete with notes)
 */
export function useUpdateSectionProgress(sessionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: error fallback centralized here; SectionCompletionCard catch is sole caller.
    meta: { errorFallback: "Failed to update section progress" },
    mutationFn: async (input: {
      sectionName: string;
      is_complete: boolean;
      notes?: string;
    }) => {
      const { sectionName, ...body } = input;
      const response = await apiClient.put<{ data: SectionProgress }>(
        `/audit/sessions/${sessionId}/section-progress/${sectionName}`,
        body,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["section-progress", sessionId],
      });
      queryClient.invalidateQueries({ queryKey: ["audit-session", sessionId] });
    },
  });
}

// ============================================================================
// Resident Forms
// ============================================================================

export interface ResidentFormSubmission {
  id: string;
  form_template_id: string;
  resident_id: string;
  employee_id: string | null;
  facility_id: string;
  submission_date: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: Record<string, any>;
  notes: string | null;
  template_version: number;
  submitted_by: string;
  created_at: string;
  updated_at: string;
  template?: {
    id: string;
    name: string;
    description: string | null;
    audience: string;
    frequency: string;
    schema: unknown;
    html_template: string | null;
  };
  employee?: {
    id: string;
    first_name: string;
    last_name: string;
    position: string;
  };
}

/**
 * Fetch form submissions for a specific resident
 */
export function useResidentForms(
  facilityId: string | undefined,
  residentId: string | undefined,
  /**
   * F-103 — the caller may already know this request cannot succeed. The
   * resident page renders the grant's scopes and fired this anyway on plain
   * page load, producing a correct 403 that the global handler turned into an
   * alarming toast. Optional and defaulting to enabled, so no existing caller
   * changes behaviour.
   */
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ["resident-forms", facilityId, residentId],
    queryFn: async () => {
      if (!facilityId || !residentId)
        throw new Error("Facility ID and Resident ID required");
      const response = await apiClient.get<{
        data: { form_submissions: ResidentFormSubmission[] };
      }>(`/audit/facilities/${facilityId}/residents/${residentId}/forms`);
      return response.data.data.form_submissions;
    },
    enabled:
      !!facilityId && !!residentId && options?.enabled !== false,
  });
}
